import { useContext, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';

import AssignedRolesTable from '@atom/components/common/assignedRoles/AssignedRolesTable';
import AssignRolesModal from '@atom/components/common/assignedRoles/AssignRolesModal';
import { USER_UPDATE } from '@atom/graph/user';
import { Icon, IconButton } from '@atom/mui';
import { AccountCircleIcon, AddIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { pluralize } from '@atom/utilities/stringUtilities';

import UserContext from '../UserContext';

import classes from './rolesTab.module.css';

const RolesTab = () => {
  const {
    user: { id, roles, inheritedRoles },
    refetch,
  } = useContext(UserContext);

  const [addRolesModalOpen, setAddRolesModalOpen] = useState<boolean>(false);

  const [updateUser, { loading }] = useMutation(USER_UPDATE);

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleAssignRoles = async (roles: string[]) => {
    await updateUser({
      variables: {
        input: {
          id,
          roles,
        },
      },
    });

    setAddRolesModalOpen(false);
    refetch();
  };

  // set of roles applied directly to the user (not inherited)
  const userRoleIds = useMemo(() => {
    return new Set(
      roles.filter(role => !role?.inheritedFrom?.length).map(role => role.id),
    );
  }, [roles, inheritedRoles]);

  const handleDelete = (roleId: string) => {
    handleAssignRoles(
      roles.filter(role => role.id !== roleId).map(role => role.id),
    );
  };

  const allRoles = [...roles, ...inheritedRoles];

  return (
    <>
      <div className={classes.headerContainer}>
        <div>
          {!allRoles.length
            ? 'No roles'
            : `${allRoles.length} ${pluralize(allRoles.length, 'role')}`}
        </div>
        {hasRolePermissions(ROLE_SETS.ORG_ADMIN) && (
          <IconButton onClick={() => setAddRolesModalOpen(true)}>
            <Icon icon={AddIcon} />
          </IconButton>
        )}
      </div>
      {!roles.length ? (
        <div className={classes.noRolesContainer}>
          <Icon
            color={colors.neutral.silver}
            style={{ fontSize: '80px' }}
            icon={AccountCircleIcon}
          />
          <div className={classes.noRoles}>There are no roles</div>
        </div>
      ) : (
        <AssignedRolesTable roles={allRoles} onDelete={handleDelete} />
      )}
      <AssignRolesModal
        open={addRolesModalOpen}
        onClose={() => setAddRolesModalOpen(false)}
        onConfirm={handleAssignRoles}
        loading={loading}
        existingRoles={userRoleIds}
      />
    </>
  );
};

export default RolesTab;
